export const GRID_COLUMNS = 6;
export const GRID_ROWS = 5;
export const GRID_PAGE_SIZE = GRID_COLUMNS * GRID_ROWS;
export const INVENTORY_PAGE_COUNT = 4;
export const GRID_SIZE = GRID_PAGE_SIZE * INVENTORY_PAGE_COUNT;

const isSpriteStripUrl = (rawUrl) => /\d+s\.png$/i.test(rawUrl || '');

export { isSpriteStripUrl };

export const buildPublicImageUrl = (rawUrl) => {
  if (!rawUrl) return '';
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://') || rawUrl.startsWith('data:')) {
    return rawUrl;
  }
  return rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
};

export const ItemImage = ({ imageUrl, alt, className = '', singleFrameSprite = false }) => {
  if (!imageUrl) return null;
  const src = buildPublicImageUrl(imageUrl);
  if (isSpriteStripUrl(imageUrl)) {
    const spriteClassName = `item-sprite-icon ${singleFrameSprite ? 'item-sprite-icon--single-frame' : ''} ${className}`.trim();
    return (
      <span
        className={spriteClassName}
        role="img"
        aria-label={alt}
        style={{ backgroundImage: `url(${src})` }}
      />
    );
  }
  return <img className={className} src={src} alt={alt} />;
};

export const getItemGridSize = (slotItem) => {
  if (slotItem?.slotType === 'armor') {
    return { width: 1, height: 2 };
  }
  const rawW = Number(slotItem?.baseItem?.gridWidth || 1);
  const rawH = Number(slotItem?.baseItem?.gridHeight || 1);
  const width = Number.isFinite(rawW) ? Math.max(1, Math.min(rawW, GRID_COLUMNS)) : 1;
  const height = Number.isFinite(rawH) ? Math.max(1, Math.min(rawH, GRID_ROWS)) : 1;
  return { width, height };
};

export const getFootprintIndexes = (anchorIndex, width, height, gridSize = GRID_SIZE) => {
  if (!Number.isInteger(anchorIndex) || anchorIndex < 0 || anchorIndex >= gridSize) return null;
  const pageStart = Math.floor(anchorIndex / GRID_PAGE_SIZE) * GRID_PAGE_SIZE;
  const localIndex = anchorIndex - pageStart;
  const row = Math.floor(localIndex / GRID_COLUMNS);
  const col = localIndex % GRID_COLUMNS;
  if (col + width > GRID_COLUMNS || row + height > GRID_ROWS) return null;

  const indexes = [];
  for (let dy = 0; dy < height; dy += 1) {
    for (let dx = 0; dx < width; dx += 1) {
      indexes.push(pageStart + ((row + dy) * GRID_COLUMNS) + col + dx);
    }
  }
  return indexes.every((idx) => idx < gridSize) ? indexes : null;
};

export const computePageCoverage = (pageSlots, pageStartIndex, gridSize = GRID_SIZE) => {
  const coveredIndexes = new Set();
  const spanByIndex = {};

  pageSlots.forEach((slotItem, localIndex) => {
    if (!slotItem) return;
    const absoluteIndex = pageStartIndex + localIndex;
    if (coveredIndexes.has(absoluteIndex)) return;

    const { width, height } = getItemGridSize(slotItem);
    const footprint = getFootprintIndexes(absoluteIndex, width, height, gridSize);
    if (!footprint) {
      spanByIndex[absoluteIndex] = { width: 1, height: 1 };
      return;
    }

    spanByIndex[absoluteIndex] = { width, height };
    footprint.slice(1).forEach((idx) => coveredIndexes.add(idx));
  });

  return { coveredIndexes, spanByIndex };
};

export const buildSpanGridStyle = (span) => ({
  gridColumnEnd: `span ${span.width}`,
  gridRowEnd: `span ${span.height}`,
  aspectRatio: `${span.width} / ${span.height}`,
  zIndex: 2
});

export const isSlotLocked = (index, unlockedCount) => (
  Number.isInteger(index) && index >= unlockedCount
);

export const getSlotIndexFromGridPoint = (x, y, gridAreaElement, currentPage) => {
  if (!gridAreaElement || !Number.isInteger(currentPage) || currentPage < 1) return null;
  const pageGrid = gridAreaElement.querySelector('.inventory-grid-page.active');
  if (!pageGrid) return null;

  const gridRect = pageGrid.getBoundingClientRect();
  if (x < gridRect.left || x > gridRect.right || y < gridRect.top || y > gridRect.bottom) {
    return null;
  }

  const slots = [...pageGrid.querySelectorAll('[data-slot-index]')];
  for (const slot of slots) {
    const rect = slot.getBoundingClientRect();
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
      const slotIndex = Number(slot.dataset.slotIndex);
      if (Number.isInteger(slotIndex) && slotIndex >= 0 && slotIndex < GRID_SIZE) {
        return slotIndex;
      }
    }
  }

  const refSlot = slots.find((slot) => !slot.classList.contains('multi-size')) || slots[0];
  if (!refSlot) return null;

  const refRect = refSlot.getBoundingClientRect();
  const style = window.getComputedStyle(pageGrid);
  const columnGap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
  const rowGap = parseFloat(style.rowGap) || parseFloat(style.gap) || 0;
  const paddingLeft = parseFloat(style.paddingLeft) || 0;
  const paddingTop = parseFloat(style.paddingTop) || 0;
  const stepX = refRect.width + columnGap;
  const stepY = refRect.height + rowGap;
  if (stepX <= 0 || stepY <= 0) return null;

  const localX = x - gridRect.left - paddingLeft;
  const localY = y - gridRect.top - paddingTop;
  if (localX < 0 || localY < 0) return null;

  const col = Math.min(GRID_COLUMNS - 1, Math.max(0, Math.floor(localX / stepX)));
  const row = Math.min(GRID_ROWS - 1, Math.max(0, Math.floor(localY / stepY)));
  const pageStart = (currentPage - 1) * GRID_PAGE_SIZE;
  return pageStart + (row * GRID_COLUMNS) + col;
};

export const resolveInventoryDropAnchor = ({
  dropIndex,
  movingItem,
  canPlaceAtAnchor
}) => {
  if (!Number.isInteger(dropIndex) || dropIndex < 0 || dropIndex >= GRID_SIZE || !movingItem) {
    return null;
  }

  const { width, height } = getItemGridSize(movingItem);
  const pageStart = Math.floor(dropIndex / GRID_PAGE_SIZE) * GRID_PAGE_SIZE;
  const dropLocal = dropIndex - pageStart;
  const candidates = [];

  for (let dy = 0; dy < height; dy += 1) {
    for (let dx = 0; dx < width; dx += 1) {
      const anchorLocal = dropLocal - (dy * GRID_COLUMNS) - dx;
      if (anchorLocal < 0 || anchorLocal >= GRID_PAGE_SIZE) continue;
      const anchorRow = Math.floor(anchorLocal / GRID_COLUMNS);
      const anchorCol = anchorLocal % GRID_COLUMNS;
      if (anchorCol + width > GRID_COLUMNS || anchorRow + height > GRID_ROWS) continue;
      candidates.push({
        anchorIndex: pageStart + anchorLocal,
        distance: dy + dx
      });
    }
  }

  candidates.sort((a, b) => a.distance - b.distance);
  for (const { anchorIndex } of candidates) {
    if (canPlaceAtAnchor(anchorIndex)) {
      return anchorIndex;
    }
  }
  return null;
};
